import type {
  ApiResponse,
  Neighbor,
  WorldDataSource,
  WorldMap,
  XpProfile,
} from './types';

const XP_PER_COURSE = 50;
const XP_PER_LEVEL = 100;

/**
 * Mock data source backed by static JSON files under /mock.
 * Keeps profile state in memory so the "complete lesson → XP → house upgrade"
 * loop is demonstrable offline. Replaced by the World_Api client in Phase 1.
 */
export class MockApiClient implements WorldDataSource {
  private profile: XpProfile | null = null;

  private async fetchJson<T>(path: string): Promise<T> {
    const res = await fetch(path);
    if (!res.ok) {
      throw new Error(`Mock fetch failed: ${path} (HTTP ${res.status})`);
    }
    const body = (await res.json()) as ApiResponse<T>;
    if (!body.success) {
      throw new Error(`Mock response not successful: ${path} — ${body.message}`);
    }
    return body.data;
  }

  async getWorldMap(): Promise<WorldMap> {
    return this.fetchJson<WorldMap>('./mock/world-map.json');
  }

  async getXpProfile(): Promise<XpProfile> {
    if (!this.profile) {
      this.profile = await this.fetchJson<XpProfile>('./mock/xp-profile.json');
    }
    return this.profile;
  }

  async getNeighbors(): Promise<Neighbor[]> {
    return this.fetchJson<Neighbor[]>('./mock/neighbors.json');
  }

  async completeCourse(_courseId: number): Promise<XpProfile> {
    const p = await this.getXpProfile();
    p.totalXp += XP_PER_COURSE;
    p.currentXp += XP_PER_COURSE;
    while (p.currentXp >= p.nextLevelXp) {
      p.currentXp -= p.nextLevelXp;
      p.level += 1;
      p.nextLevelXp = XP_PER_LEVEL;
    }
    return p;
  }
}
